import React, { FC } from 'react';
import styled from 'styled-components';
import { Container, CardsContainer } from '../BaseCardTileList/style';
import { Base, Img, Link } from 'styled';
import {
  Container as ArticleCardContainer,
  TitleContainer,
  SimplifiedDescription,
} from '../BaseCardTile/style';
import BrowsePagePagination from 'styled/components/BrowsePagePagination';
import { BlogPost } from 'types';
import sanityImgUtil from 'utils/sanityImgUtil';
import sanityImgSrcSetUtil from 'utils/sanityImgSrcSetUtil';
import { HERO_COMPRESSION_VALUE } from '../BlogPostHero';
import formatAriaLabelWithTitle from 'utils/formatAriaLabelWithTitle';
import Head from 'next/head';
import { BLOG_CATEGORY_PAGE_SIZE } from 'lib/constants';
import useBlogBase from 'hooks/useBlogBase';
import LoadingSkeleton from 'styled/components/LoadingSkeleton';

export const CardContainer = styled(ArticleCardContainer)`
  height: auto;
  min-height: auto;
`;

export const TextContainer = styled(Base)`
  padding: 1.5rem;
`;

const DateParagraph = styled.div`
  margin: 0.75rem 0 0;
  ${({ theme }) => theme.typography.variants.primary.xs};
`;

type Props = {
  blogPosts: Array<BlogPost>;
  blogPostCount: number;
  isLoading?: boolean;
  language?: string;
  slug: string;
  page: number;
  paginate?: boolean;
};

const BlogPostCardTileList: FC<Props> = ({
  blogPosts,
  blogPostCount,
  isLoading,
  slug,
  page,
  paginate,
}) => {
  const { blogBase } = useBlogBase();
  if (!blogPosts.length) return null;

  const totalPages = Math.ceil(blogPostCount / BLOG_CATEGORY_PAGE_SIZE);
  const hasNextPage = page < totalPages;
  const hasPrevPage = page !== 1;
  const prevPage = page - 1;

  return (
    <>
      {paginate && (
        <Head>
          {hasPrevPage && (
            <link
              rel="prev"
              href={`${blogBase}${slug}${
                prevPage === 1 ? '' : `?page=${prevPage}`
              }`}
            />
          )}
          {hasNextPage && (
            <link rel="next" href={`${blogBase}${slug}?page=${page + 1}`} />
          )}
        </Head>
      )}
      <Container>
        <CardsContainer>
          {blogPosts.map((blogPost) => {
            const category = blogPost?.categories?.[0] || '';

            return (
              <CardContainer
                key={blogPost.id}
                cardTileHasImage
                className="disabled-ads"
              >
                {isLoading ? (
                  <LoadingSkeleton />
                ) : (
                  <>
                    <Img
                      alt={blogPost.heroImage.alt}
                      sizes="(max-width: 768px) 1536px, (max-width: 1440px) 2880px, 1500px"
                      src={sanityImgUtil(
                        blogPost.heroImage,
                        1536,
                        undefined,
                        HERO_COMPRESSION_VALUE,
                      )}
                      srcSet={sanityImgSrcSetUtil(
                        blogPost.heroImage,
                        1536,
                        2880,
                        1500,
                        HERO_COMPRESSION_VALUE,
                      )}
                      preloadFullWidth={true}
                      dimensions={blogPost.heroImage.metadata?.dimensions}
                      crop={blogPost.heroImage.crop}
                    />
                    <TextContainer>
                      <TitleContainer>
                        <Link
                          variant="hover-underline-black"
                          label={blogPost.title}
                          ariaLabel={formatAriaLabelWithTitle(blogPost.title)}
                          to={`${blogBase}${category}${blogPost.slug}`}
                          wrap={true}
                        />
                      </TitleContainer>
                      {blogPost.seo.description && (
                        <SimplifiedDescription
                          $maxLines={blogPost.seo.description.length}
                        >
                          {blogPost.seo.description}
                        </SimplifiedDescription>
                      )}
                      <DateParagraph>
                        {new Date(
                          blogPost.meta.updatedDate ||
                            blogPost.meta.publishedDate,
                        ).toLocaleDateString('en-US', {
                          month: 'long',
                          day: 'numeric',
                          year: 'numeric',
                        })}
                      </DateParagraph>
                    </TextContainer>
                  </>
                )}
              </CardContainer>
            );
          })}
        </CardsContainer>
        {paginate && (
          <BrowsePagePagination pageNumber={page} totalPages={totalPages} />
        )}
      </Container>
    </>
  );
};

export default BlogPostCardTileList;
